import React from 'react';
import { 
  Volume2, 
  Waves, 
  Mic2, 
  Music2, 
  Share2, 
  Settings2,
  LucideIcon,
  Activity,
  Library,
  Upload,
  Zap
} from 'lucide-react';
import { SimulationConfig } from '@/types';
import { AudioVolumeSection } from './AudioVolumeSection';
import { AudioWorkshopTab } from './AudioWorkshopTab';
import { AudioPostProcessingSection } from './AudioPostProcessingSection';
import { CustomSoundUploadSection } from './CustomSoundUploadSection';
import { SongConverterSection } from './SongConverterSection';
import { RhythmicMidiFilterSection } from './RhythmicMidiFilterSection';
import { MelodyLibrarySection } from './MelodyLibrarySection';
import { motion } from 'motion/react';
import { CollapsibleSection } from './CollapsibleSection';

interface AudioStudioTabProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAnalyzing: boolean;
  analysisProgress: number;
  onBallSoundUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingBallSound: boolean;
  cloudSaves?: any[];
  isLocked?: boolean;
  getLockInfo: (id: string) => { isLocked: boolean; min_plan?: string };
  onUpgradeClick?: () => void;
}

export const AudioStudioTab: React.FC<AudioStudioTabProps> = ({ 
  config, setConfig, onFileUpload, isAnalyzing, analysisProgress, onBallSoundUpload, isUploadingBallSound,
  cloudSaves = [], getLockInfo, onUpgradeClick
}) => (
  <motion.div 
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex flex-col w-full"
  >
    {/* Master Controls */}
    <CollapsibleSection 
        title="Volume & Mixer" 
        icon={Volume2} 
        darkMode={config.darkMode}
        settingKey="audioMaster"
        isLocked={getLockInfo('audioMaster').isLocked}
        lockRequiredPlan={getLockInfo('audioMaster').min_plan as any}
        onUpgradeClick={onUpgradeClick}
        defaultOpen
    >
        <div className="flex flex-col gap-8 mt-4">
            <AudioVolumeSection config={config} setConfig={setConfig} />
            <div className="h-px bg-zinc-500/10" />
            <AudioPostProcessingSection config={config} setConfig={setConfig} cloudSaves={cloudSaves} />
        </div>
    </CollapsibleSection>

    {/* Synthesis Workshop */}
    <CollapsibleSection 
        title="Synthesis & Melodies" 
        icon={Waves} 
        darkMode={config.darkMode}
        settingKey="audioWorkshop"
        isLocked={getLockInfo('audioWorkshop').isLocked}
        lockRequiredPlan={getLockInfo('audioWorkshop').min_plan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-8 mt-4">
            <AudioWorkshopTab config={config} setConfig={setConfig} />
            <div className="h-px bg-zinc-500/10" />
            <RhythmicMidiFilterSection config={config} setConfig={setConfig} />
            <div className="h-px bg-zinc-500/10" />
            <MelodyLibrarySection config={config} setConfig={setConfig} />
        </div>
    </CollapsibleSection>

    {/* Sample Handling */}
    <CollapsibleSection 
        title="Sample Processing & Uploads" 
        icon={Mic2} 
        darkMode={config.darkMode}
        settingKey="audioSamples"
        isLocked={getLockInfo('audioSamples').isLocked}
        lockRequiredPlan={getLockInfo('audioSamples').min_plan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-8 mt-4">
            <CustomSoundUploadSection config={config} setConfig={setConfig} onBallSoundUpload={onBallSoundUpload} isUploadingBallSound={isUploadingBallSound} />
            <div className="h-px bg-zinc-500/10" />
            <SongConverterSection config={config} onFileUpload={onFileUpload} isAnalyzing={isAnalyzing} analysisProgress={analysisProgress} />
        </div>
    </CollapsibleSection>
  </motion.div>
);
